import { ABI_VERSION, MAX_SYSTEM_TEXT_ADVANCES, MAX_SYSTEM_TEXT_LINES, MAX_SYSTEM_TEXT_METRIC_INSTRUCTIONS, MAX_SYSTEM_TEXT_METRICS_BYTES, PROTOCOL_ALIGNMENT, STREAM_HEADER_BYTES, SYSTEM_TEXT_METRIC_LAYOUTS, SYSTEM_TEXT_METRICS_MAGIC, SystemTextMetricOpcode, INSTRUCTION_FLAG_MASK, INSTRUCTION_FLAG_OPTIONAL, INSTRUCTION_LENGTH_ESCAPE, INSTRUCTION_HEADER_BYTES, MINIMUM_READABLE_ABI_VERSION, } from "./generated";
/** Deterministic system-font metric contract violation. */
export class SystemTextMetricError extends Error {
    constructor(message) {
        super(message);
        this.name = "SystemTextMetricError";
    }
}
/** Encodes a canonical Host-to-Core system-font measurement batch. */
export function encodeSystemTextMetricBatch(deltas) {
    if (deltas.length > MAX_SYSTEM_TEXT_METRIC_INSTRUCTIONS)
        fail("too many text metric deltas");
    const bytes = STREAM_HEADER_BYTES +
        deltas.reduce((total, delta) => {
            const opcode = delta.type === "upsert"
                ? SystemTextMetricOpcode.UpsertSystemTextMetric
                : SystemTextMetricOpcode.ReleaseSystemTextMetric;
            const advances = delta.type === "upsert" ? delta.metric.advances.length : 0;
            const positional = delta.type === "upsert" ? delta.metric.positionalAdvances.length : 0;
            const contractions = delta.type === "upsert" ? delta.metric.contractions.length : 0;
            return checkedAdd(total, checkedAdd(instructionBytes(opcode), checkedAdd(advances * 8, checkedAdd(positional * 4, contractions * 16))));
        }, 0);
    if (bytes > MAX_SYSTEM_TEXT_METRICS_BYTES)
        fail("text metric batch exceeds maximum size");
    const output = new Uint8Array(bytes);
    const writer = new Writer(output);
    writer.u32(SYSTEM_TEXT_METRICS_MAGIC);
    writer.u16(ABI_VERSION);
    writer.u16(STREAM_HEADER_BYTES);
    writer.u32(bytes);
    writer.u32(deltas.length);
    const seen = new Set();
    for (const delta of deltas) {
        const metric = delta.type === "upsert" ? delta.metric : { stringId: delta.stringId, styleId: delta.styleId };
        validateId(metric.stringId, "stringId");
        validateId(metric.styleId, "styleId");
        const key = pairKey(metric.stringId, metric.styleId);
        if (seen.has(key))
            fail("text metric resource pair occurs more than once in a batch");
        seen.add(key);
        if (delta.type === "upsert") {
            validateMetric(delta.metric);
            writer.instruction(SystemTextMetricOpcode.UpsertSystemTextMetric);
            writer.u32(delta.metric.stringId);
            writer.u32(delta.metric.styleId);
            writer.f32(delta.metric.maxLineWidth);
            writer.u32(delta.metric.lineCount);
            writer.u32(delta.metric.advances.length);
            for (const [codePoint, advance] of delta.metric.advances) {
                writer.u32(codePoint);
                writer.f32(advance);
            }
            writer.u32(delta.metric.positionalAdvances.length);
            for (const advance of delta.metric.positionalAdvances)
                writer.f32(advance);
            writer.u32(delta.metric.contractions.length);
            for (const [first, second, contraction, firstDelta] of delta.metric.contractions) {
                writer.u32(first);
                writer.u32(second);
                writer.f32(contraction);
                writer.f32(firstDelta);
            }
        }
        else {
            writer.instruction(SystemTextMetricOpcode.ReleaseSystemTextMetric);
            writer.u32(delta.stringId);
            writer.u32(delta.styleId);
        }
    }
    writer.closeInstruction();
    if (writer.offset !== output.byteLength)
        fail("text metric encoder length mismatch");
    return output;
}
/** Decodes and fully validates a Host-to-Core system-font measurement batch. */
export function decodeSystemTextMetricBatch(input) {
    if (!(input instanceof Uint8Array))
        fail("text metric batch must be Uint8Array bytes");
    if (input.byteLength > MAX_SYSTEM_TEXT_METRICS_BYTES)
        fail("text metric batch exceeds maximum size");
    if (input.byteLength % PROTOCOL_ALIGNMENT !== 0)
        fail("text metric batch is not aligned");
    const reader = new Reader(input);
    if (reader.u32() !== SYSTEM_TEXT_METRICS_MAGIC)
        fail("wrong text metric magic");
    // Newer producers stay readable through the self-describing instruction
    // framing; anything older than it cannot be stepped through safely.
    if (reader.u16() < MINIMUM_READABLE_ABI_VERSION)
        fail("unsupported text metric ABI version");
    if (reader.u16() !== STREAM_HEADER_BYTES)
        fail("invalid text metric header length");
    if (reader.u32() !== input.byteLength)
        fail("text metric length does not match input");
    const declaredCount = reader.u32();
    if (declaredCount > MAX_SYSTEM_TEXT_METRIC_INSTRUCTIONS)
        fail("too many text metric deltas");
    if (declaredCount > Math.floor(reader.remaining / 12)) {
        fail("text metric count cannot fit in the remaining bytes");
    }
    const deltas = [];
    const seen = new Set();
    while (reader.remaining !== 0) {
        const start = reader.offset;
        const header = reader.instruction();
        if (!isKnownOpcode(SystemTextMetricOpcode, header.opcode)) {
            if (!header.optional)
                fail(`unknown text metric opcode ${String(header.opcode)}`);
            reader.seekTo(header.end);
            continue;
        }
        const opcode = systemTextMetricOpcode(header.opcode);
        const stringId = reader.u32();
        const styleId = reader.u32();
        validateId(stringId, "stringId");
        validateId(styleId, "styleId");
        const key = pairKey(stringId, styleId);
        if (seen.has(key))
            fail("text metric resource pair occurs more than once in a batch");
        seen.add(key);
        if (opcode === SystemTextMetricOpcode.UpsertSystemTextMetric) {
            const maxLineWidth = reader.f32();
            const lineCount = reader.u32();
            const advanceCount = reader.u32();
            if (advanceCount > MAX_SYSTEM_TEXT_ADVANCES) {
                fail("text metric advance count is outside the supported limit");
            }
            // Bound against the bytes that remain before allocating: a declared count
            // must never size an array ahead of the payload backing it.
            if (advanceCount > Math.floor(reader.remaining / 8))
                fail("truncated text metric batch");
            const advances = [];
            for (let index = 0; index < advanceCount; index += 1) {
                advances.push(Object.freeze([reader.codePoint(), reader.f32()]));
            }
            const positionalCount = reader.u32();
            if (positionalCount > MAX_SYSTEM_TEXT_ADVANCES) {
                fail("text metric advance count is outside the supported limit");
            }
            if (positionalCount > Math.floor(reader.remaining / 4))
                fail("truncated text metric batch");
            const positionalAdvances = [];
            for (let index = 0; index < positionalCount; index += 1) {
                positionalAdvances.push(reader.f32());
            }
            const contractionCount = reader.u32();
            if (contractionCount > MAX_SYSTEM_TEXT_ADVANCES) {
                fail("text metric contraction count is outside the supported limit");
            }
            if (contractionCount > Math.floor(reader.remaining / 16)) {
                fail("truncated text metric batch");
            }
            const contractions = [];
            for (let index = 0; index < contractionCount; index += 1) {
                contractions.push(Object.freeze([
                    reader.codePoint(),
                    reader.codePoint(),
                    reader.f32(),
                    reader.f32(),
                ]));
            }
            const metric = {
                stringId,
                styleId,
                maxLineWidth,
                lineCount,
                advances: Object.freeze(advances),
                positionalAdvances: Object.freeze(positionalAdvances),
                contractions: Object.freeze(contractions),
            };
            validateMetric(metric);
            deltas.push({ type: "upsert", metric: Object.freeze(metric) });
        }
        else if (opcode === SystemTextMetricOpcode.ReleaseSystemTextMetric) {
            deltas.push({ type: "release", stringId, styleId });
        }
        else {
            fail(`unknown text metric opcode ${String(opcode)}`);
        }
        const actual = reader.offset - start;
        if (actual < instructionBytes(opcode))
            fail("text metric instruction length mismatch");
        // A declared length that disagrees with what was consumed would let a
        // skipping reader and this one disagree about where the next one starts.
        if (reader.offset !== header.end)
            fail("instruction length does not match its payload");
    }
    if (deltas.length !== declaredCount)
        fail("text metric instruction count does not match");
    return deltas;
}
function validateMetric(metric) {
    validateId(metric.stringId, "stringId");
    validateId(metric.styleId, "styleId");
    if (!Number.isFinite(metric.maxLineWidth) ||
        !Number.isFinite(Math.fround(metric.maxLineWidth)) ||
        metric.maxLineWidth < 0) {
        fail("text metric width must be finite, non-negative, and representable as f32");
    }
    if (!Number.isInteger(metric.lineCount) ||
        metric.lineCount < 1 ||
        metric.lineCount > MAX_SYSTEM_TEXT_LINES) {
        fail("text metric line count is outside the supported limit");
    }
    if (metric.advances.length > MAX_SYSTEM_TEXT_ADVANCES) {
        fail("text metric advance count is outside the supported limit");
    }
    if (metric.positionalAdvances.length > MAX_SYSTEM_TEXT_ADVANCES) {
        fail("text metric advance count is outside the supported limit");
    }
    for (const advance of metric.positionalAdvances) {
        if (!Number.isFinite(advance) || !Number.isFinite(Math.fround(advance)) || advance < 0) {
            fail("text metric advance must be finite, non-negative, and representable as f32");
        }
    }
    if (metric.contractions.length > MAX_SYSTEM_TEXT_ADVANCES) {
        fail("text metric contraction count is outside the supported limit");
    }
    let previousPair = [-1, -1];
    for (const [first, second, contraction, firstDelta] of metric.contractions) {
        for (const value of [contraction, firstDelta]) {
            if (!Number.isFinite(value) || !Number.isFinite(Math.fround(value))) {
                fail("text metric contraction must be finite and representable as f32");
            }
        }
        validateCodePoint(first);
        validateCodePoint(second);
        if (first < previousPair[0] || (first === previousPair[0] && second <= previousPair[1])) {
            fail("text metric contractions must ascend by code-point pair without duplicates");
        }
        previousPair = [first, second];
    }
    let previous = -1;
    for (const [codePoint, advance] of metric.advances) {
        if (!Number.isFinite(advance) || !Number.isFinite(Math.fround(advance)) || advance < 0) {
            fail("text metric advance must be finite, non-negative, and representable as f32");
        }
        validateCodePoint(codePoint);
        // Ascending and unique keeps one table one byte sequence, so a golden
        // fixture and a cross-language round trip still pin the encoding.
        if (codePoint <= previous) {
            fail("text metric advances must ascend by code point without duplicates");
        }
        previous = codePoint;
    }
}
function validateCodePoint(value) {
    if (!Number.isInteger(value) ||
        value < 0 ||
        value > 0x10ffff ||
        (value >= 0xd800 && value <= 0xdfff)) {
        fail("text metric advance code point is not a Unicode scalar value");
    }
}
function validateId(value, label) {
    if (!Number.isInteger(value) || value < 1 || value > 0xffff_ffff) {
        fail(`${label} must be a non-zero unsigned 32-bit integer`);
    }
}
function pairKey(stringId, styleId) {
    return `${String(stringId)}:${String(styleId)}`;
}
function systemTextMetricOpcode(value) {
    if (value === Number(SystemTextMetricOpcode.UpsertSystemTextMetric)) {
        return SystemTextMetricOpcode.UpsertSystemTextMetric;
    }
    if (value === Number(SystemTextMetricOpcode.ReleaseSystemTextMetric)) {
        return SystemTextMetricOpcode.ReleaseSystemTextMetric;
    }
    return fail(`unknown text metric opcode ${String(value)}`);
}
/** Bytes an instruction occupies before any variable-size payload. */
function instructionBytes(opcode) {
    const layout = SYSTEM_TEXT_METRIC_LAYOUTS[opcode];
    if (layout === undefined)
        fail("unknown text metric opcode");
    return layout.minimumBytes;
}
class Reader {
    bytes;
    #offset = 0;
    constructor(bytes) {
        this.bytes = bytes;
    }
    get offset() {
        return this.#offset;
    }
    get remaining() {
        return this.bytes.byteLength - this.#offset;
    }
    /** Reads one instruction header, including where the instruction ends. */
    instruction() {
        const offset = this.#offset;
        if (offset % PROTOCOL_ALIGNMENT !== 0)
            fail("instruction is not aligned");
        this.require(INSTRUCTION_HEADER_BYTES);
        const opcode = this.u8();
        const flags = this.u8();
        if ((flags & ~INSTRUCTION_FLAG_MASK) !== 0)
            fail("unsupported instruction flags");
        const words = this.u16();
        const length = words === INSTRUCTION_LENGTH_ESCAPE ? this.u32() : words * PROTOCOL_ALIGNMENT;
        const end = offset + length;
        if (length < INSTRUCTION_HEADER_BYTES || length % PROTOCOL_ALIGNMENT !== 0) {
            fail("instruction length is invalid");
        }
        if (end > this.bytes.byteLength)
            fail("instruction length runs past the stream");
        return { opcode, optional: (flags & INSTRUCTION_FLAG_OPTIONAL) !== 0, end };
    }
    /** Moves the cursor forward to an instruction boundary. */
    seekTo(offset) {
        if (offset < this.#offset || offset > this.bytes.byteLength)
            fail("invalid instruction skip");
        this.#offset = offset;
    }
    u8() {
        this.require(1);
        return this.bytes[this.#offset++] ?? 0;
    }
    u16() {
        this.require(2);
        const value = new DataView(this.bytes.buffer, this.bytes.byteOffset + this.#offset, 2).getUint16(0, true);
        this.#offset += 2;
        return value;
    }
    u32() {
        this.require(4);
        const value = new DataView(this.bytes.buffer, this.bytes.byteOffset + this.#offset, 4).getUint32(0, true);
        this.#offset += 4;
        return value;
    }
    /** Reads a Unicode scalar value; surrogates and out-of-range values fail. */
    codePoint() {
        const value = this.u32();
        validateCodePoint(value);
        return value;
    }
    f32() {
        this.require(4);
        const value = new DataView(this.bytes.buffer, this.bytes.byteOffset + this.#offset, 4).getFloat32(0, true);
        this.#offset += 4;
        if (!Number.isFinite(value))
            fail("text metric float must be finite");
        return value;
    }
    require(bytes) {
        if (bytes > this.remaining)
            fail("truncated text metric batch");
    }
}
class Writer {
    bytes;
    #offset = 0;
    #instructionStart;
    constructor(bytes) {
        this.bytes = bytes;
    }
    get offset() {
        return this.#offset;
    }
    instruction(opcode) {
        this.closeInstruction();
        this.#instructionStart = this.#offset;
        this.u8(opcode);
        this.u8(0);
        this.u16(0);
    }
    /**
     * Writes the length of the instruction that just ended.
     *
     * These instructions are fixed-size and small, so the escape a variable-size
     * stream needs cannot arise here; it is still refused rather than truncated.
     */
    closeInstruction() {
        const start = this.#instructionStart;
        if (start === undefined)
            return;
        this.#instructionStart = undefined;
        const words = (this.#offset - start) / PROTOCOL_ALIGNMENT;
        if (words >= INSTRUCTION_LENGTH_ESCAPE)
            fail("text metric instruction is too large");
        new DataView(this.bytes.buffer, this.bytes.byteOffset + start + 2, 2).setUint16(0, words, true);
    }
    u8(value) {
        this.bytes[this.#offset++] = value;
    }
    u16(value) {
        new DataView(this.bytes.buffer, this.bytes.byteOffset + this.#offset, 2).setUint16(0, value, true);
        this.#offset += 2;
    }
    u32(value) {
        new DataView(this.bytes.buffer, this.bytes.byteOffset + this.#offset, 4).setUint32(0, value, true);
        this.#offset += 4;
    }
    f32(value) {
        new DataView(this.bytes.buffer, this.bytes.byteOffset + this.#offset, 4).setFloat32(0, value, true);
        this.#offset += 4;
    }
}
function checkedAdd(left, right) {
    const result = left + right;
    if (!Number.isSafeInteger(result))
        fail("text metric length overflow");
    return result;
}
function fail(message) {
    throw new SystemTextMetricError(message);
}
/** Whether an opcode byte names a member this build knows. */
function isKnownOpcode(values, value) {
    return typeof values[value] === "string";
}
//# sourceMappingURL=system-text-metrics.js.map